import { antibioticL2Targets, AssayInfo, GetGroup, L2TargetOrAssay } from '@resistapp/common/assays';
import { BaseColors } from '@resistapp/common/base-styles';
import { get10FoldChangeInVolume, getRelative10FoldChange } from '@resistapp/common/statistics/fold-change';
import {
  getResistanceGeneIndexAndLevel,
  getResistanceLevel,
  ResistanceLevel,
} from '@resistapp/common/statistics/resistance-index';
import { getRiskScore } from '@resistapp/common/statistics/risk-score';
import { AbundanceMode, FullAbundance, MetricMode, ProcessMode } from '@resistapp/common/types';
import { interpolateRgb, piecewise } from 'd3-interpolate';
import { isNil } from 'lodash';
import { theme } from '../components/shared/theme';
import { getBeforeOrAfterAbundances, OverviewDatum } from '../data-utils/plot-data/build-overview-line-data';
import { resistanceLevelMetadata } from '../data-utils/resistance-level';
import { getOverviewConfiguration, overviewChartConfigurations } from './overview-chart-configurations';

export type MetricAndLevel = [number | null, ResistanceLevel | null];

export function getMetricAndLevel(
  _datum: { beforeAbundances: FullAbundance[] | undefined; afterAbundances: FullAbundance[] | undefined } | undefined,
  _targets: L2TargetOrAssay[],
  metricMode: MetricMode,
  processMode: ProcessMode,
  activeAbundanceMode: AbundanceMode,
  getGroup: GetGroup,
  allAssays: AssayInfo[],
  useArgsInsteadOfSingleGene = false,
): MetricAndLevel {
  const datum = _datum || { beforeAbundances: undefined, afterAbundances: undefined };
  const targets =
    useArgsInsteadOfSingleGene &&
    _targets.length === 1 &&
    _targets[0].startsWith('AY') &&
    metricMode !== MetricMode.REDUCTION
      ? antibioticL2Targets
      : _targets;

  if (metricMode === MetricMode.ARGI) {
    const abundances = getBeforeOrAfterAbundances(datum, processMode);
    const { resistanceIndex, resistanceLevel } = getResistanceGeneIndexAndLevel(abundances, targets, getGroup);
    return [resistanceIndex, resistanceLevel];
  } else if (metricMode === MetricMode.RISK) {
    const abundances = getBeforeOrAfterAbundances(datum, processMode);
    const riskScore = getRiskScore(abundances as FullAbundance[], targets, getGroup);
    return [riskScore, null];
  } else {
    if (processMode !== ProcessMode.DURING || !datum.beforeAbundances || !datum.afterAbundances) {
      return [null, null];
    }
    if (activeAbundanceMode === AbundanceMode.COPIES_PER_L) {
      const foldChangeInVol = get10FoldChangeInVolume(
        datum.beforeAbundances,
        datum.afterAbundances,
        targets.length === 1 ? targets[0] : undefined,
        allAssays,
      );
      return [foldChangeInVol, null];
    } else {
      const relativeFoldChange = getRelative10FoldChange(
        datum.beforeAbundances,
        datum.afterAbundances,
        targets.length === 1 ? targets[0] : undefined,
        allAssays,
      );
      return [relativeFoldChange, null];
    }
  }
}

// Type definition for the color scale configuration points
export interface ColorScalePoint {
  value: number; // Normalized value (0 to 1)
  color: string;
}

// Export the configuration
const blendedColor = interpolateRgb(BaseColors.blue400, BaseColors.red300);
export const riskScoreColorScaleConfig: ColorScalePoint[] = [
  { value: 0, color: BaseColors.blue900 }, // Blue at 0
  { value: 0.06, color: blendedColor(0.5) },
  { value: 0.1, color: blendedColor(0.75) },
  { value: 0.11, color: BaseColors.red300 },
  { value: 1.0, color: theme.colors.riskScore100 }, // Red at 100
];

export interface DynamicLegendConfig {
  colorScaleConfig: ColorScalePoint[];
  mapLegendTicks: string[];
  gradientMultiplier: number;
  isSwOnlyScale: boolean;
}

/**
 * Determines the appropriate color scale, ticks, and gradient multiplier for the risk score legend
 * based on whether any data point in the provided data OR the European mean has a risk score > 10.
 */
export function getDynamicRiskScoreLegendConfig(
  data: OverviewDatum[] | undefined,
  defaultTicks: string[],
  selectedTargets: L2TargetOrAssay[],
  processMode: ProcessMode,
  activeAbundanceMode: AbundanceMode,
  getGroup: GetGroup,
  allAssays: AssayInfo[],
  referenceLevels?: number[],
): DynamicLegendConfig {
  const relevantData = data?.filter(d => d.afterAbundances?.length || d.beforeAbundances?.length) || [];

  const hasRiskScoreOverTenInData = relevantData.some(datum => {
    const [metric] = getMetricAndLevel(
      datum,
      selectedTargets,
      MetricMode.RISK,
      processMode,
      activeAbundanceMode,
      getGroup,
      allAssays,
    );
    return metric !== null && metric > 10;
  });

  const refLevelIsOverTen = !!referenceLevels?.find(level => level > 10);

  // Use the 0-10 scale only if NEITHER the data nor the European mean exceeds 10
  const isSwOnlyScale = !hasRiskScoreOverTenInData && !refLevelIsOverTen;

  if (isSwOnlyScale) {
    return {
      colorScaleConfig: riskScoreColorScaleConfig.filter(point => point.value <= 0.1),
      mapLegendTicks: ['0', '2', '4', '6', '8', '10'],
      gradientMultiplier: 1000, // Multiply by 1000 because range is 0-10 (0.1 * 1000 = 100%)
      isSwOnlyScale: true,
    };
  } else {
    return {
      colorScaleConfig: riskScoreColorScaleConfig,
      mapLegendTicks: defaultTicks,
      gradientMultiplier: 100, // Multiply by 100 because range is 0-100 (1.0 * 100 = 100%)
      isSwOnlyScale: false,
    };
  }
}

const colorScales = {
  // we force the return type here, since in piecewise it's "any"
  [MetricMode.REDUCTION]: (t: number): string => {
    const colorScale = overviewChartConfigurations[MetricMode.REDUCTION][AbundanceMode.COPIES_ANALYSED]?.colorScale;
    const zeroPoint = colorScale ? colorScale[1] / 100 : 0.83;

    if (t === zeroPoint) {
      return '#FFFFFF';
    } else if (t > zeroPoint) {
      // Positive values (0 to 1) - red gradient
      const redNormalized = (t - zeroPoint) / (1 - zeroPoint);
      return piecewise(interpolateRgb, ['#FFFFFF', theme.colors.reductionMinus100])(redNormalized) as string;
    } else {
      // Negative values (-5 to 0) - blue gradient
      const blueNormalized = t / zeroPoint;
      return piecewise(interpolateRgb, [theme.colors.reduction100, '#FFFFFF'])(blueNormalized) as string;
    }
  },
  [MetricMode.RISK]: (t: number): string => {
    // Edge cases
    if (t <= riskScoreColorScaleConfig[0].value) return riskScoreColorScaleConfig[0].color;
    const lastPoint = riskScoreColorScaleConfig[riskScoreColorScaleConfig.length - 1];
    if (t >= lastPoint.value) return lastPoint.color;

    // Find and work with the index of the starting point of the segment t falls into
    const segmentIndex = riskScoreColorScaleConfig.findIndex(
      (point, index, arr) => index + 1 < arr.length && t >= point.value && t <= arr[index + 1].value,
    );
    if (segmentIndex === -1) {
      // Fallback (shouldn't be reached if config is correct and edge cases handled)
      console.error('Failed to find a color segment for the risk score', t);
      return lastPoint.color;
    }
    const segmentConfig = riskScoreColorScaleConfig[segmentIndex];
    const nextSegmentConfig = riskScoreColorScaleConfig[segmentIndex + 1];
    // Calculate the position of t within this segment (0 to 1)
    const segmentT = (t - segmentConfig.value) / (nextSegmentConfig.value - segmentConfig.value);
    // Interpolate between the segment's start and end colors
    return interpolateRgb(segmentConfig.color, nextSegmentConfig.color)(segmentT);
  },
};
export function getMetricColor(
  value: number | null,
  metricMode: MetricMode,
  abundanceMode: AbundanceMode,
  forMap = false,
): string {
  if (value === null) {
    return forMap ? theme.colors.neutral400 : theme.colors.neutral700;
  } else if (metricMode === MetricMode.ARGI) {
    const level = getResistanceLevel(value);
    return resistanceLevelMetadata[level].color;
  }

  const minValue = getOverviewConfiguration(metricMode, abundanceMode).detailBarGraphMin;
  const maxValue = getOverviewConfiguration(metricMode, abundanceMode).detailBarGraphMax();
  const normalizedValue = (value - minValue) / (maxValue - minValue);

  return colorScales[metricMode](normalizedValue);
}

export function friendlyMetricValue(
  metric: number | null,
  metricMode: MetricMode,
  activeAbundanceMode: AbundanceMode,
): string {
  if (isNil(metric)) return '-';
  return getOverviewConfiguration(metricMode, activeAbundanceMode).getFriendlyMetricValue(metric) || '-';
}

export function getReductionMetricTextColor(value: number | null, _abundanceMode: AbundanceMode): string {
  if (value === null) {
    return 'black';
  }

  return value >= 0.5 || value <= -2.5 ? 'white' : 'black';
}
